import React, { useState } from "react";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Grid,
  TextField,
  Switch,
  FormControlLabel,
  Paper,
} from "@mui/material";

interface ClientProcessConfig {
  [key: string]: any;
}

interface ClientProcessFormProps {
  config: ClientProcessConfig;
  handleChange: (path: string, value: any) => void;
}

const tabSections = [
  "Settings",
  "Phone Sync",
  "Auto Scan",
  "Leg Fold",
  "Phone Camera",
  "Preferences",
  "Wizard",
  "Overlay Order",
];

const sectionFields: Record<string, string[]> = {
  Settings: [
    "url",
    "name",
    "s3_bucket",
    "ums_environment",
    "location_id",
    "vin_length",
    "vin_scan_terminology",
    "vin_feedback_terminology",
    "vin_length_err_terminology",
    "prefix_removal",
    "is_default",
    "is_display_tablet_rooftop",
    "use_lighting_controller",
  ],
  "Phone Sync": ["phone_sync", "phone_sync_terminology"],
  "Auto Scan": [
    "auto_scan_enabled",
    "auto_scan_pano",
    "auto_scan_countdown_length",
    "manual_scan_countdown_length",
    "display_screen_sleep_timer",
    "tablet_rooftop_select",
    "skip_interior_simultaneous",
  ],
  "Leg Fold": [
    "actuator_fold_trigger_distance",
    "actuator_leg_stagger_time",
    "actuator_trigger_count_threshold",
    "actuator_leg_cycle_time",
  ],
  "Phone Camera": ["phone_camera_resolution", "phone_camera_lens"],
  Preferences: [
    "use_nhtsa_database",
    "allow_cancel_vehicle",
    "dual_hand_scan_capture",
    "preferances.vinchar",
    "preferances.vinBypass",
    "preferances.skipExternal",
    "preferances.skipPano",
  ],
  Wizard: ["enabled", "disabled"],
  "Overlay Order": ["overlays"],
};

const ClientProcessForm: React.FC<ClientProcessFormProps> = ({
  config,
  handleChange,
}) => {
  const [tab, setTab] = useState(0);

  const isNumericField = (key: string, value: any) =>
    ["location_id", "vin_length", "prefix_removal"].includes(key) ||
    typeof value === "number";

  const renderField = (key: string, value: any) => {
    const type = typeof value;

    // ✅ Switch fields
    if (type === "boolean") {
      return (
        <FormControlLabel
          key={key}
          control={
            <Switch
              checked={value}
              onChange={(e) => handleChange(key, e.target.checked)}
            />
          }
          label={key}
        />
      );
    }

    // ✅ Text or number-like fields (no spinners)
    if (type === "number" || type === "string") {
      const numeric = isNumericField(key, value);
      return (
        <TextField
          key={key}
          fullWidth
          label={key}
          value={value ?? ""}
          type="text"
          inputProps={
            numeric
              ? { inputMode: "numeric", pattern: "[0-9]*" }
              : undefined
          }
          onChange={(e) => handleChange(key, e.target.value)}
        />
      );
    }

    // ✅ JSON fields (nested objects)
    return (
      <TextField
        key={key}
        fullWidth
        multiline
        label={key}
        value={JSON.stringify(value, null, 2)}
        onChange={(e) => {
          try {
            const parsed = JSON.parse(e.target.value);
            handleChange(key, parsed);
          } catch {
            // ignore malformed JSON
          }
        }}
      />
    );
  };

  const renderFieldsForSection = (section: string) => {
    const fields = sectionFields[section] || [];
    return fields.map((fieldKey) => {
      const keys = fieldKey.split(".");
      const value = keys.reduce((obj, k) => obj?.[k], config);
      return (
        <Grid item xs={12} sm={6} key={fieldKey}>
          {renderField(fieldKey, value)}
        </Grid>
      );
    });
  };

  return (
    <Paper sx={{ p: 3 }}>
      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={(e, newValue) => setTab(newValue)}
        variant="scrollable"
        scrollButtons="auto"
        TabIndicatorProps={{ style: { backgroundColor: "red" } }}
        sx={{
          mb: 3,
          borderBottom: 1,
          borderColor: "divider",
          "& .MuiTab-root": { textTransform: "none", fontWeight: "bold" },
          "& .Mui-selected": { color: "red" },
        }}
      >
        {tabSections.map((label) => (
          <Tab key={label} label={label.toUpperCase()} />
        ))}
      </Tabs>

      <Typography variant="h6" sx={{ mb: 2 }}>
        {tabSections[tab]} Settings
      </Typography>

      <Grid container spacing={2}>
        {renderFieldsForSection(tabSections[tab])}
      </Grid>
    </Paper>
  );
};

export default ClientProcessForm;
